import { useId, useMemo, useState } from "react";

import { emailTypeLabel, readableEmailType } from "./emailTypes";
import {
  eventTitleOf,
  formatSentAt,
  recipientsOf,
  senderNameOf,
  type EmailSend,
} from "./sendHistory";

const COLUMNS = ["Email", "Event", "Sent to", "Sent by", "When"];

function labelOf(send: EmailSend): string {
  return emailTypeLabel(send.type) ?? readableEmailType(send.type);
}

/** "12 of 14 people", with ", 2 didn't go through" in red after it. */
function SentTo({ send }: { send: EmailSend }) {
  return (
    <>
      {send.sent} of {recipientsOf(send, send.recipients)}
      {send.failed > 0 ? (
        <span className="text-[color:var(--color-danger)]">
          , {send.failed} didn't go through
        </span>
      ) : null}
    </>
  );
}

/**
 * "Emails already sent": every logged send to everyone, newest first.
 * It shows the chosen event's emails unless the sender asks for every
 * event. Per-person "Place confirmed" emails only show with every event,
 * so they don't bury the sends to everyone.
 */
export function SentHistory({
  history,
  isLoading,
  loadFailed,
  eventSlug,
  eventTitle,
}: {
  history: readonly EmailSend[] | undefined;
  isLoading: boolean;
  loadFailed: boolean;
  /** The event chosen above; null when there is none. */
  eventSlug: string | null;
  eventTitle: string | null;
}) {
  const headingId = useId();
  const [everyEvent, setEveryEvent] = useState(false);
  const showingAll = everyEvent || eventSlug === null;

  const rows = useMemo(
    () =>
      (history ?? []).filter(
        (send) =>
          showingAll ||
          (send.eventSlug === eventSlug && send.type !== "place_confirmed"),
      ),
    [history, showingAll, eventSlug],
  );

  return (
    <section
      aria-labelledby={headingId}
      className="mt-12 border-t border-[color:var(--color-line)] pt-8"
    >
      <h2
        id={headingId}
        className="text-title font-bold text-[color:var(--color-ink)]"
      >
        Emails already sent
      </h2>

      {isLoading ? (
        <p className="mt-3 text-[16px] text-[color:var(--color-ink-soft)]">
          Loading the emails already sent…
        </p>
      ) : loadFailed ? (
        <p className="mt-3 text-[16px] text-[color:var(--color-danger)]">
          Couldn't load the emails already sent. Check your internet connection
          and refresh the page.
        </p>
      ) : (history ?? []).length === 0 ? (
        <p className="mt-3 text-[16px] text-[color:var(--color-ink-soft)]">
          Nothing sent yet. Emails you send to everyone will be listed here.
        </p>
      ) : (
        <>
          {eventSlug === null ? null : (
            <div className="mt-2 flex flex-wrap items-center gap-x-4">
              <p className="text-[15px] text-[color:var(--color-ink-soft)]">
                {everyEvent
                  ? "Showing emails for every event."
                  : `Showing emails for ${eventTitle ?? "this event"}.`}
              </p>
              <button
                type="button"
                onClick={() => setEveryEvent((v) => !v)}
                className="min-h-11 text-[15px] font-semibold text-[color:var(--color-amber)] underline underline-offset-4"
              >
                {everyEvent ? "Show only this event" : "Show every event"}
              </button>
            </div>
          )}

          {rows.length === 0 ? (
            <p className="mt-3 text-[16px] text-[color:var(--color-ink-soft)]">
              No emails have gone out for this event yet.
            </p>
          ) : (
            <>
              <div className="mt-4 hidden md:block">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[color:var(--color-line)]">
                      {COLUMNS.map((column) => (
                        <th
                          key={column}
                          scope="col"
                          className="table-head py-3 pr-6"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((send) => (
                      <tr
                        key={send.id}
                        className="border-b border-[color:var(--color-line)] align-top"
                      >
                        <td className="py-3 pr-6 text-[14px] font-semibold text-[color:var(--color-ink)]">
                          {labelOf(send)}
                        </td>
                        <td className="py-3 pr-6 text-[14px] text-[color:var(--color-ink-soft)]">
                          {eventTitleOf(send)}
                        </td>
                        <td className="py-3 pr-6 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
                          <SentTo send={send} />
                        </td>
                        <td className="py-3 pr-6 text-[14px] text-[color:var(--color-ink-soft)]">
                          {senderNameOf(send) ?? "Not recorded"}
                        </td>
                        <td className="py-3 pr-6 text-[14px] whitespace-nowrap text-[color:var(--color-ink-soft)] tabular-nums">
                          {formatSentAt(send.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="mt-4 space-y-3 md:hidden">
                {rows.map((send) => (
                  <li key={send.id} className="surface px-4 py-3">
                    <p className="text-[15px] font-semibold text-[color:var(--color-ink)]">
                      {labelOf(send)}, {eventTitleOf(send)}
                    </p>
                    <p className="mt-1 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
                      <SentTo send={send} /> ·{" "}
                      {senderNameOf(send) ?? "Not recorded"} ·{" "}
                      {formatSentAt(send.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
